/**
 * CRS Scorer Page - trigger a new Capital Readiness score for a startup or project.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, PlayCircle, Building2, Briefcase } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import capitalReadinessService from '../services/capitalReadinessService';
import projectService from '../services/projectService';
import dataService, { CompanyRecord } from '../services/data';
import { Project } from '../types/project';

type EntityKind = 'project' | 'startup';

interface EntityOption {
  kind: EntityKind;
  id: number;
  label: string;
}

const CRSScorerPage: React.FC = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<EntityKind>('project');
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [scoring, setScoring] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [projRes, compRes] = await Promise.all([
        projectService.fetchProjects({ size: 200 } as any),
        dataService.fetchCompanies(),
      ]);
      const opts: EntityOption[] = [];
      if (projRes.data?.projects) {
        projRes.data.projects.forEach((p: Project) =>
          opts.push({
            kind: 'project',
            id: p.id,
            label: p.project_name,
          })
        );
      }
      if (compRes.data) {
        compRes.data.forEach((c: CompanyRecord) =>
          opts.push({ kind: 'startup', id: c.id, label: c.name })
        );
      }
      setOptions(opts);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = options.filter((o) => o.kind === kind);

  const handleScore = async () => {
    if (!selectedId) {
      toast.error('Select an entity');
      return;
    }
    setScoring(true);
    const payload =
      kind === 'project'
        ? { project_id: Number(selectedId) }
        : { startup_id: Number(selectedId) };
    const res = await capitalReadinessService.scoreEntity(payload);
    setScoring(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      toast.success('Score generated');
      navigate(`/capital-readiness/scores/${res.data.id}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <Link
          to="/capital-readiness"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to capital readiness
        </Link>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">Score an entity</h1>
          <p className="text-sm text-gray-600 mt-1">
            Run the 7-dimension readiness analysis on a startup or project.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-2">
              Entity type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  { value: 'project', label: 'Project', Icon: Briefcase },
                  { value: 'startup', label: 'Startup', Icon: Building2 },
                ] as Array<{ value: EntityKind; label: string; Icon: any }>
              ).map((opt) => {
                const Icon = opt.Icon;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${
                      kind === opt.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="entity_kind"
                      checked={kind === opt.value}
                      onChange={() => {
                        setKind(opt.value);
                        setSelectedId('');
                      }}
                    />
                    <Icon className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">
                      {opt.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Select {kind}
            </label>
            {loading ? (
              <div className="flex items-center text-sm text-gray-600 py-2">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading {kind}s...
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) =>
                  setSelectedId(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Choose a {kind}...</option>
                {filtered.map((o) => (
                  <option key={`${o.kind}-${o.id}`} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            {!loading && filtered.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No {kind}s found.
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={handleScore}
              disabled={scoring || !selectedId}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {scoring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4 mr-2" />
              )}
              Score Now
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CRSScorerPage;
